import React, { useEffect, useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import useFetching from "../../utils/Fetching";
import JSZip from "jszip";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import UserScheduleDeleteDialog from "./UserScheduleDeleteDialog";

const UserTable = () => {
  const [userData, setUserData] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const { adminShowUser, adminDeleteUser } = useFetching();

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminShowUser();
      const checkList = {};
      response.forEach((user) => {
        checkList[user.user_id] = user.scheduledDeletion !== null;
      });
      setUserData(response);
      setChecked(checkList);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Fehler beim Laden der User-Daten:", error);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleDownload = async (userId) => {
    const user = userData.find((user) => user.user_id === userId);
    const parsedUserId = userId.match(/\D*0*(\d+)@/)[1];
    const zip = new JSZip();

    const userName = `${user.baseData.name.slice(0, 1)}${
      user.baseData.surname
    }`;

    // PDF-Dateien hinzufügen
    user.signed.forEach((pdf) => {
      const pdfData = pdf.fileData;
      zip.file(
        pdf.fileName.slice(0, pdf.fileName.length - 4) +
          "_" +
          userName +
          ".pdf",
        pdfData,
        { base64: true }
      );
    });

    // licenseFront hinzufügen, falls vorhanden
    if (user.licenseFront !== null) {
      const licenseB64 = user.licenseFront.replace(
        /^data:image\/(png|jpg);base64,/,
        ""
      );
      zip.file("licenseFront" + "_" + userName + ".png", licenseB64, {
        base64: true,
      });
    }

    // licenseBack hinzufügen, falls vorhanden
    if (user.licenseBack !== null) {
      const licenseB64 = user.licenseBack.replace(
        /^data:image\/(png|jpg);base64,/,
        ""
      );
      zip.file("licenseBack" + "_" + userName + ".png", licenseB64, {
        base64: true,
      });
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `onboarding_${user.baseData.name.slice(0, 1)}${
      user.baseData.surname
    }_${parsedUserId}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCheckboxChange = (userId) => {
    setChecked((checked) => ({
      ...checked,
      [userId]: !checked[userId],
    }));
  };

  const handleDelete = async (userId) => {
    await adminDeleteUser(userId);
    loadUsers();
  };

  return (
    <TableContainer
      className={`container-table ${loading ? "loading" : ""}`}
      component={Paper}
    >
      {userData && !loading && (
        <Table className="table">
          <caption>
            * Nach dem Abspeichern der Nutzerdaten können die Daten zum löschen
            markiert werden. Nach 14 Tagen werden markierte Datensätze
            unwiederruflich gelöscht.
          </caption>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Fortschritt</TableCell>
              <TableCell>Dateien</TableCell>
              <TableCell>Abschließen *</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userData &&
              userData.map((user) => {
                return (
                  <TableRow key={`user-data-entry-${user.user_id}`}>
                    <TableCell>
                      <Tooltip placement="bottom" title={user.user_id}>
                        <span>
                          {user.baseData ? user.baseData.name : user.user_id}{" "}
                          {user.baseData && user.baseData.surname}
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>{user.step ? user.step + 1 : "0"} / 5</TableCell>
                    <TableCell>
                      {(user.signed.length > 0 || user.license !== null) && (
                        <Tooltip
                          placement="right"
                          title={
                            <React.Fragment>
                              <Typography>Dateien:</Typography>
                              <ul className="tooltip-list">
                                {user.signed &&
                                  user.signed.map((signed, index) => (
                                    <Typography
                                      component="li"
                                      key={`user-documents-${index}`}
                                    >
                                      {signed.fileName}
                                    </Typography>
                                  ))}
                                {user.licenseFront && (
                                  <Typography component="li">
                                    Führerscheinvorderseite
                                  </Typography>
                                )}
                                {user.licenseBack && (
                                  <Typography component="li">
                                    Führerscheinrückseite
                                  </Typography>
                                )}
                              </ul>
                            </React.Fragment>
                          }
                        >
                          <Button
                            variant="contained"
                            endIcon={<FileDownloadIcon />}
                            onClick={() => handleDownload(user.user_id)}
                            disabled={user.step + 1 !== 5}
                          >
                            Download
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="usertable-actions">
                        <input
                          type="checkbox"
                          onChange={() => handleCheckboxChange(user.user_id)}
                          value={checked[user.user_id]}
                          checked={checked[user.user_id]}
                          disabled={user.scheduledDeletion !== null}
                        />
                        {checked[user.user_id] &&
                          user.scheduledDeletion === null && (
                            <UserScheduleDeleteDialog
                              onSubmit={() => handleDelete(user.user_id)}
                            />
                          )}
                        {user.scheduledDeletion !== null && (
                          <span className="delete-message">
                            Nutzerdaten werden am{" "}
                            <Tooltip
                              title={new Date(
                                user.scheduledDeletion
                              ).toLocaleTimeString()}
                            >
                              <span>
                                {new Date(
                                  user.scheduledDeletion
                                ).toLocaleDateString()}
                              </span>
                            </Tooltip>{" "}
                            gelöscht
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      )}
      {loading && <div className="loading-spinner small"></div>}
    </TableContainer>
  );
};

export default UserTable;
